import * as React from "react";

// material-ui
import makeStyles from "@material-ui/styles/makeStyles";
import Card from "@material-ui/core/Card";

import Divider from "@material-ui/core/Divider";

import { PageHeader, PageFooter, SimpleShowLayout, TextField, FunctionField, BackButton, EditButton } from "jazasoft";
import CardHeader from "../../../components/CardHeader";
import { isTrue, fieldOptions, listFromCsv } from "../../../utils/helpers";

import MuiTable from "../../../components/MuiTable";
import useQuery from "../../../hooks/useQuery";
import useUnit from "../../../hooks/useUnit";
import SettingKeys from "../../../utils/SettingKeys";
import { useEntityPrivilege, useSetting } from "../../../hooks";
import { ProductFields } from "../../../utils/entityFields";
import { Role } from "../../../utils/types";

const columns = [
  { dataKey: "productName", title: "Product" },
  { dataKey: "ratio", title: "Ratio" }
];

const useStyles = makeStyles({
  content: {
    margin: "1.5em"
  },
  pageFooter: {
    "& > *": {
      marginRight: "1em"
    }
  }
});

const ProductView = props => {
  const classes = useStyles();
  const { id, i18nKey, resource, basePath, roles } = props;
  const { disableEdit } = useEntityPrivilege("product");

  // api call
  const { data: product = {} } = useQuery({ url: `products/${id}` });
  const { unitChoices: unitList } = useUnit(product);

  const fields = useSetting(SettingKeys.ENTITY_FIELDS, resource);
  return (
    <div>
      <PageHeader title={`Product #${id}`} />

      <div className={classes.content}>
        <Card>
          <CardHeader title="Basic Info" />
          <Divider />
          <SimpleShowLayout style={{ padding: "1em" }} record={product} i18nKey={i18nKey} footer={false}>
            {fields.includes(ProductFields.NAME) && <TextField source="name" {...fieldOptions(3)} />}
            {fields.includes(ProductFields.BUNDLE_PARTS) && <TextField source="bundleParts" {...fieldOptions(3)} />}
            {fields.includes(ProductFields.REPLACEMENT_PANELS) && <TextField source="replacementPanels" {...fieldOptions(3)} />}
            {fields.includes(ProductFields.SERIAL_RESET_AT) && <TextField source="serialResetAt" {...fieldOptions(3)} />}
            {fields.includes(ProductFields.BUNDLE_RESET_AT) && <TextField source="bundleResetAt" {...fieldOptions(3)} />}
            {fields.includes(ProductFields.BUNDLE_BREAK_AT) && <TextField source="bundleBreakAt" {...fieldOptions(3)} />}
            {fields.includes(ProductFields.BUNDLE_SIZE_SORT) && (
              <FunctionField
                source="bundleSizeSort"
                render={record =>
                  record?.bundleSizeSort?.toLowerCase() === "asc"
                    ? "Ascending"
                    : record?.bundleSizeSort?.toLowerCase() === "desc"
                    ? "Descending"
                    : record.bundleSizeSort
                }
                {...fieldOptions(3)}
              />
            )}
            {fields.includes(ProductFields.BUNDLE_SERIAL_SORT) && <TextField source="bundleSerialSort" {...fieldOptions(3)} />}
            {unitList?.length > 1 && (
              <FunctionField
                source="unitIds"
                label="Units"
                render={record =>
                  unitList
                    .filter(u => listFromCsv(record?.unitIds).includes(u.id))
                    .map(u => u.name)
                    .join(", ")
                }
                {...fieldOptions(3)}
              />
            )}

            <FunctionField source="isSet" label="Is Set?" render={record => (isTrue(record?.isSet) ? "Yes" : "No")} {...fieldOptions(3)} />
            {fields.includes(ProductFields.max_BOM_CU) && <TextField source="maxBomCu" />}
            {fields.includes(ProductFields.DESC) && <TextField source="desc" {...fieldOptions(3)} />}
          </SimpleShowLayout>
        </Card>

        {isTrue(product?.isSet) && (
          <Card style={{ marginTop: "1em" }}>
            <CardHeader title="Products in Set" />
            <Divider />
            <div>
              <MuiTable columns={columns} rows={product?.productList?.map(ps => ({ productName: ps.product?.name, ratio: ps.ratio }))} />
            </div>
          </Card>
        )}

        <PageFooter className={classes.pageFooter}>
          <BackButton variant="contained" basePath={basePath} resource={resource} />
          {(roles?.includes(Role.MASTER) || roles?.includes(Role.SUPER_USER)) && !disableEdit && (
            <EditButton variant="contained" color="primary" basePath={basePath} resource={resource} to="edit" />
          )}
        </PageFooter>
      </div>
    </div>
  );
};

export default ProductView;
